import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-background-50">
      {/* Hero Section */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20golden%20abstract%20flowing%20waves%20texture%20with%20soft%20peach%20and%20cream%20tones%2C%20elegant%20minimal%20background%20with%20metallic%20gold%20subtle%20shimmer%20accents%2C%20sophisticated%20editorial%20aesthetic%2C%20no%20people%2C%20abstract%20art%20style%2C%20gentle%20gradients%20with%20warm%20ambient%20lighting&width=1600&height=700&seq=about-hero-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-32 flex flex-col items-center text-center">
          <p className="text-accent-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-4">
            Our Story
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 max-w-3xl leading-tight">
            More Than Jewellery.<br />A Promise.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-background-50/80 font-label max-w-2xl leading-relaxed">
            Joviaa was built for women who want gold-look jewellery that survives real life — the sweat, the rain, the rush, the moment.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-16">
          <ScrollReveal direction="left" className="w-full lg:w-[480px] flex-shrink-0">
            <div className="w-full aspect-[4/5] rounded-xl overflow-hidden bg-secondary-200">
              <img
                src="https://readdy.ai/api/search-image?query=Soft%20warm%20editorial%20photography%20of%20gold%20layered%20necklaces%20on%20cream%20silk%20fabric%20drape%2C%20natural%20golden%20hour%20sunlight%20casting%20gentle%20shadows%2C%20minimal%20elegant%20composition%2C%20luxurious%20jewellery%20flat%20lay%20with%20organic%20textures%2C%20rich%20warm%20amber%20and%20sand%20tones%2C%20no%20people%2C%20artistic%20still%20life&width=960&height=1200&seq=about-story-01&orientation=portrait"
                alt="Joviaa jewellery on silk"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" className="flex-1">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-5">
              Why Joviaa Exists
            </h2>
            <div className="flex flex-col gap-4 text-foreground-600 font-label leading-relaxed text-sm md:text-base">
              <p>
                We looked at the jewellery market and saw two extremes: cheap imitation pieces that tarnish within weeks, lose their colour after one sweaty commute, and leave green marks on your skin — and on the other end, fine gold and vermeil jewellery that is beautiful but too precious (and too expensive) to wear every single day without worry.
              </p>
              <p>
                There was nothing in the middle. Nothing that looked like real gold, felt premium, and could actually keep up with the pace of a modern woman&apos;s life — the office, the gym, the weekend trip, the spontaneous beach plan, the wedding after-party. Jewellery is meant to be worn, not preserved in a velvet box.
              </p>
              <p>
                So we built Joviaa. A brand that makes gold-look jewellery you never have to take off — engineered in 316L stainless steel with PVD 18K gold-tone plating that resists sweat, water, and daily wear. Affordable enough to buy for yourself, premium enough to gift with pride.
              </p>
              <p className="font-heading font-semibold text-primary-500 text-base md:text-lg">
                This is Life-Proof Gold. This is Joviaa.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Our Material Promise */}
      <section className="bg-accent-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-accent-600 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
                Built Different
              </p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
                Our Material Promise
              </h2>
              <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
                Every Joviaa piece is built on three material principles that set us apart from mass-market imitation jewellery.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Card 1: 316L Steel */}
            <ScrollReveal delay={0}>
              <div className="bg-background-50 rounded-xl p-6 md:p-8 text-center group hover:bg-background-50 transition-colors">
                <div className="w-16 h-16 rounded-full bg-accent-200 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-shield-flash-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-primary-500 mb-3">
                  316L Stainless Steel Base
                </h3>
                <p className="text-sm text-foreground-600 font-label leading-relaxed">
                  Unlike brass or copper-based imitation jewellery, every Joviaa piece starts with surgical-grade 316L stainless steel. It doesn&apos;t corrode, doesn&apos;t oxidise, and keeps its structural integrity through sweat, water, and years of daily wear.
                </p>
              </div>
            </ScrollReveal>

            {/* Card 2: PVD Plating */}
            <ScrollReveal delay={120}>
              <div className="bg-background-50 rounded-xl p-6 md:p-8 text-center group hover:bg-background-50 transition-colors">
                <div className="w-16 h-16 rounded-full bg-accent-200 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-sun-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-primary-500 mb-3">
                  PVD 18K Gold-Tone Plating
                </h3>
                <p className="text-sm text-foreground-600 font-label leading-relaxed">
                  We use Physical Vapour Deposition (PVD) — the same technology used in luxury watches — to bond 18K gold-tone colour directly into the steel at a molecular level. No paint, no lacquer, no peeling. The gold finish is part of the metal itself.
                </p>
              </div>
            </ScrollReveal>

            {/* Card 3: Hypoallergenic */}
            <ScrollReveal delay={240}>
              <div className="bg-background-50 rounded-xl p-6 md:p-8 text-center group hover:bg-background-50 transition-colors">
                <div className="w-16 h-16 rounded-full bg-accent-200 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-heart-pulse-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-primary-500 mb-3">
                  Nickel-Free &amp; Hypoallergenic
                </h3>
                <p className="text-sm text-foreground-600 font-label leading-relaxed">
                  Nickel is the most common cause of jewellery allergies and skin irritation. We formulate every piece to be completely nickel-free and hypoallergenic — so even the most sensitive skin can wear Joviaa all day, every day, without a reaction.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Values Strip */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <ScrollReveal>
          <div className="text-center mb-12 md:mb-16">
            <p className="text-foreground-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
              What We Stand For
            </p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
              The Joviaa Values
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          <ScrollReveal delay={0}>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-5">
                <i className="ri-time-line text-xl text-background-50"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">
                Durability
              </h3>
              <p className="text-sm text-foreground-600 font-label leading-relaxed max-w-xs mx-auto">
                We believe jewellery should be lived in, not locked up. Every piece is built to survive sweat, water, travel, and time — because your life doesn&apos;t pause for your accessories.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-5">
                <i className="ri-palette-line text-xl text-background-50"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">
                Design
              </h3>
              <p className="text-sm text-foreground-600 font-label leading-relaxed max-w-xs mx-auto">
                We bridge Korean minimalism, Indian heritage, and global street style to create pieces that feel fresh, modern, and intentional — never generic. Every design goes through multiple iterations before it reaches you.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={240}>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-5">
                <i className="ri-hand-heart-line text-xl text-background-50"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">
                Accessibility
              </h3>
              <p className="text-sm text-foreground-600 font-label leading-relaxed max-w-xs mx-auto">
                Premium shouldn&apos;t mean unattainable. We price our jewellery so you can build a collection over time — not save up for one piece a year. Gold-look luxury, within reach.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Banner */}
      <ScrollReveal>
        <section className="bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                Ready to experience Life-Proof Gold?
              </h2>
              <p className="mt-2 text-sm md:text-base text-background-50/70 font-label">
                Browse our collections — we think you&apos;ll like what you find.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 whitespace-nowrap flex-wrap">
              <Link
                to="/shop"
                className="px-7 py-3 bg-background-50 text-primary-500 rounded-lg font-heading font-semibold text-sm hover:bg-background-50/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop All
              </Link>
              <Link
                to="/durability"
                className="px-7 py-3 border-2 border-background-50/30 text-background-50 rounded-lg font-heading font-semibold text-sm hover:border-background-50/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                Learn More
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </main>
  );
}